// Dependencies
import axios from 'axios';
import { useSnackbar } from 'notistack';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
// Core & Lab
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
// Icons
import Check from '@mui/icons-material/Check';
// Actions
import { setTranslateLocale } from './redux/actions';
// Types
import { AppState } from '../../reducers';
import { Locale } from './objects/locale';
import { LanguageLocalized } from '../language/objects/language';
import { decodeHtmlEntities } from '../translation/utils/decideHtmlEntities';
import { splitHtmlWithDOM, splitTextIntoChunks } from '../utils/texts/utils/splitHtmlWithDom';

type LocaleSelectorProps = {
    id: string,
    defaultValue?: string,
    values: (any & { [index: string]: any })[],
    all_languages?: boolean,
    size?: 'small' | 'medium',
    onTranslation: (translate_locale_id: number, text: string) => void
}

const LocaleAutoTranslateSelector: FC<LocaleSelectorProps> = ({ id, defaultValue, values, size, onTranslation, all_languages }): ReactElement | null => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [locales, setLocales] = useState<Locale[]>([]);
    const locales_activated = useSelector((state: AppState) => state.locale.list);
    const locales_list = useSelector((state: AppState) => state.locale.locales_list);
    const keys = useSelector((state: AppState) => state.header.keys);
    const current_locale = useSelector((state: AppState) => state.locale.current_locale);
    const translate_locale = useSelector((state: AppState) => state.locale.translate_locale);
    const onCurrentLocaleChange = (event: { target: { value: any; }; }) => {
        dispatch(setTranslateLocale(event.target.value));
    };
    const onRenderValue = () => {
        if (translate_locale.length > 0 && locales) {
            const text_value = [];
            for (let i = 0; i < locales.length; i++) {
                if (translate_locale.includes(locales[i].id)) {
                    text_value.push(locales[i].full_name);
                }
            }
            return text_value.join(', ');
        }
        return '';
    };

    const getTranslatedLangage = (locales: LanguageLocalized[], default_text: string) => {
        const value = locales.find((locale) => locale.localeTranslate === current_locale);
        return value?.translated_name && value?.translated_name !== '' ? value?.translated_name : default_text;
    };

    const isHtml = (text: string): boolean => /<\/?[a-z][\s\S]*>/i.test(text);
    const onTranslate = () => {
        const google_key = keys.find(k => k.identifier === 'google_api')?.value;
        const source_locale = locales?.find(l => l.id === current_locale);
        const source_code = source_locale?.language_code ?? '';

        if(!current_locale) return;

        if (!google_key || !source_code || !values[current_locale]) return;

        const sourceText = values[current_locale] ?? defaultValue;
        const isRichText = isHtml(sourceText);

        const chunks = isRichText
            ? splitHtmlWithDOM(sourceText)
            : splitTextIntoChunks(sourceText);

        translate_locale.forEach(async (target_locale_id) => {
            if (target_locale_id === current_locale) return;

            const target_locale = locales?.find(l => l.id === target_locale_id);
            if (!target_locale) return;

            try {
                const translations = await Promise.all(
                    chunks.map(chunk =>
                        axios.get('https://translation.googleapis.com/language/translate/v2', {
                            params: {
                                key: google_key,
                                q: chunk,
                                source: source_code,
                                target: target_locale.language_code,
                                format: isRichText ? 'html' : 'text'
                            }
                        }).then(res => decodeHtmlEntities(res.data.data.translations?.[0]?.translatedText))
                    )
                );

                const translatedText = translations.join('');
                enqueueSnackbar(`${t('shared.text-translated-to')} : ${target_locale.full_name}`, { variant: 'success' });
                onTranslation(target_locale_id, translatedText);

            } catch (error) {
                console.error('Translation error:', error);
                enqueueSnackbar(`${t('shared.text-not-translated-to')} : ${target_locale.full_name}`, { variant: 'error' });
            }
        });
    };

    // const onTranslate = () => {
    //     let google_key = null;
    //     for (let i = 0; i < keys.length; i++) {
    //         if (keys[i].identifier === 'google_api') {
    //             google_key = keys[i].value;
    //             break;
    //         }
    //     }
    //     let source_code = '';
    //     if (current_locale !== null && locales) {
    //         for (let i = 0; i < locales.length; i++) {
    //             if (locales[i].id === current_locale) {
    //                 source_code = locales[i].language_code;
    //             }
    //         }
    //     }
    //     if (current_locale !== null) {
    //         translate_locale.map((translate_locale_id) => {
    //             if (translate_locale_id !== current_locale) {
    //                 let target_code: null | Locale = null;
    //                 if (translate_locale !== null && locales) {
    //                     for (let i = 0; i < locales.length; i++) {
    //                         if (locales[i].id === translate_locale_id) {
    //                             target_code = locales[i];
    //                         }
    //                     }
    //                 }
    //                 if (target_code !== null) {
    //                     axios({
    //                         method: 'GET',
    //                         url: `https://translation.googleapis.com/language/translate/v2`,
    //                         params: {
    //                             key: google_key,
    //                             q: values[current_locale] ?? defaultValue,
    //                             source: source_code,
    //                             target: target_code.language_code
    //                         }
    //                     }).then((response) => {
    //                         const translatedText = decodeHtmlEntities(response.data.data.translations[0].translatedText);
    //                         if (translatedText) {
    //                             enqueueSnackbar(`${t('shared.text-translated-to')} : ${target_code?.full_name}`, { variant: 'success' });
    //                             onTranslation(translate_locale_id, translatedText);
    //                         } else {
    //                             enqueueSnackbar(`${t('shared.text-not-translated-to')} : ${target_code?.full_name}`, { variant: 'error' });
    //                         }
    //                     }).catch((error) => {
    //                         console.log('traduction error : ', error);
    //                         enqueueSnackbar(`${t('shared.text-not-translated-to')} : ${target_code?.full_name}`, { variant: 'error' });
    //                     });
    //                 }
    //             }
    //         });
    //     }
    // };
    useEffect(() => {
        if (all_languages && locales_list) {
            setLocales(locales_list);
        }
        if (!all_languages && locales_activated) {
            setLocales(locales_activated);
        }
    }, [locales_activated, locales_list, all_languages]);
    if (current_locale !== null) {
        return (
            <Grid container alignItems={'center'} spacing={2}>
                <Grid item xs={6}>
                    <FormControl size={size} fullWidth>
                        <InputLabel id={`${id}-translate-locale`}>{t('shared.translate-in')}</InputLabel>
                        <Select size={size} labelId={`${id}-translate-locale`} label={t('shared.translate-in')} multiple value={translate_locale} onChange={onCurrentLocaleChange} renderValue={onRenderValue}>
                            {locales && locales.map((locale) => current_locale !== locale.id && <MenuItem key={`${id}-translate-locale-${locale.id}`} value={locale.id}>{getTranslatedLangage(locale.LocalTranslation, locale.full_name)}{!!values[locale.id] && <Check className={'ft-locale-check'} />}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <Button disabled={translate_locale.length === 0} onClick={onTranslate}>{t('shared.translate')}</Button>
                </Grid>
            </Grid>
        );
    }
    return null;
};

export default LocaleAutoTranslateSelector;